import React from 'react';
import PropTypes from 'prop-types';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: 0,
      errorType: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorType: this.categorizeError(error)
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to monitoring service
    const errorId = this.generateErrorId();
    const timestamp = new Date().toISOString();

    this.setState({
      errorInfo,
      errorId,
      error: error,
      errorType: this.categorizeError(error)
    });

    // Log error details for debugging
    const errorDetails = {
      errorId,
      timestamp,
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack
      },
      errorInfo: {
        componentStack: errorInfo.componentStack
      },
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getUserId(),
      sessionId: this.getSessionId()
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group('🚨 Error Boundary Caught Error');
      console.error('Error Details:', errorDetails);
      console.groupEnd();
    }

    // Send to error reporting service
    this.logErrorToService(errorDetails);
  }

  categorizeError = (error) => {
    if (!error) return 'unknown';

    // Network errors
    if (error.message?.includes('Network Error') ||
      error.message?.includes('fetch') ||
      error.name === 'TypeError' && error.message?.includes('Failed to fetch')) {
      return 'network';
    }

    // Authentication errors
    if (error.message?.includes('401') ||
      error.message?.includes('Unauthorized') ||
      error.message?.includes('token')) {
      return 'auth';
    }

    // Permission errors
    if (error.message?.includes('403') ||
      error.message?.includes('Forbidden') ||
      error.message?.includes('permission')) {
      return 'permission';
    }

    // Chunk loading errors (for lazy loaded components)
    if (error.message?.includes('Loading chunk') ||
      error.message?.includes('ChunkLoadError')) {
      return 'chunk';
    }

    // Component rendering errors
    if (error.name === 'TypeError' ||
      error.name === 'ReferenceError' ||
      error.name === 'SyntaxError') {
      return 'component';
    }

    return 'unknown';
  };

  generateErrorId = () => {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  getUserId = () => {
    // Get user ID from Redux store or localStorage
    try {
      const state = window.store?.getState();
      return state?.auth?.user?.id || localStorage.getItem('userId') || 'anonymous';
    } catch {
      return 'anonymous';
    }
  };

  getSessionId = () => {
    // Get or create session ID
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  };

  logErrorToService = async (errorDetails) => {
    try {
      // Import error reporting service dynamically to avoid circular dependencies
      const { default: errorReportingService } = await import('../../services/errorReporting');

      // Send to error reporting service
      await errorReportingService.reportError(errorDetails);
    } catch (loggingError) {
      console.error('Error logging failed:', loggingError);
      // Fallback: Log to console if service fails
      console.error('Failed to log error to service:', errorDetails);
    }
  };

  handleRetry = () => {
    const { retryCount } = this.state;
    const { maxRetries = 3 } = this.props;

    if (retryCount < maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        errorId: null,
        retryCount: prevState.retryCount + 1,
        errorType: null
      }));
    }
  };

  handleReset = () => {
    // Reset error boundary state completely
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: 0,
      errorType: null
    });

    // Optionally reload the page
    if (this.props.resetOnRetry) {
      window.location.reload();
    }
  };

  render() {
    const { hasError, error, errorInfo, errorId, retryCount, errorType } = this.state;
    const {
      children,
      fallbackComponent: FallbackComponent,
      maxRetries = 3,
      showErrorDetails = process.env.NODE_ENV === 'development',
      customStyles = {}
    } = this.props;

    if (hasError) {
      // Use custom fallback component if provided
      if (FallbackComponent) {
        return (
          <FallbackComponent
            error={error}
            errorInfo={errorInfo}
            errorId={errorId}
            errorType={errorType}
            retryCount={retryCount}
            maxRetries={maxRetries}
            onRetry={this.handleRetry}
            onReset={this.handleReset}
            showErrorDetails={showErrorDetails}
          />
        );
      }

      // Default fallback UI
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          padding: '2rem',
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          margin: '1rem',
          ...customStyles
        }}>
          <div style={{ textAlign: 'center', maxWidth: '500px' }}>
            <h2 style={{
              color: '#dc3545',
              marginBottom: '1rem',
              fontSize: '1.5rem'
            }}>
              Something went wrong
            </h2>

            <p style={{
              color: '#6c757d',
              marginBottom: '1.5rem',
              lineHeight: '1.5'
            }}>
              {this.getErrorMessage(errorType)}
            </p>

            {errorId && (
              <p style={{
                fontSize: '0.875rem',
                color: '#adb5bd',
                marginBottom: '1.5rem'
              }}>
                Error ID: {errorId}
              </p>
            )}

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              {retryCount < maxRetries && (
                <button
                  onClick={this.handleRetry}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Try Again {retryCount > 0 && `(${retryCount}/${maxRetries})`}
                </button>
              )}

              <button
                onClick={this.handleReset}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Reset Page
              </button>
            </div>

            {showErrorDetails && error && (
              <details style={{
                marginTop: '2rem',
                textAlign: 'left',
                backgroundColor: '#f8f9fa',
                padding: '1rem',
                borderRadius: '4px',
                border: '1px solid #dee2e6'
              }}>
                <summary style={{
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  marginBottom: '0.5rem'
                }}>
                  Error Details (Development Only)
                </summary>
                <pre style={{
                  fontSize: '0.875rem',
                  overflow: 'auto',
                  maxHeight: '200px',
                  whiteSpace: 'pre-wrap'
                }}>
                  {error.toString()}
                  {errorInfo && errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return children;
  }

  getErrorMessage = (errorType) => {
    switch (errorType) {
      case 'network':
        return 'We\'re having trouble connecting to our servers. Please check your internet connection and try again.';
      case 'auth':
        return 'There was an issue with your authentication. Please log in again.';
      case 'permission':
        return 'You don\'t have permission to access this resource.';
      case 'chunk':
        return 'The application failed to load properly. Please refresh the page.';
      case 'component':
        return 'A component failed to render properly. Our team has been notified.';
      default:
        return 'An unexpected error occurred. Our team has been notified and is working to fix this issue.';
    }
  };
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallbackComponent: PropTypes.elementType,
  maxRetries: PropTypes.number,
  showErrorDetails: PropTypes.bool,
  resetOnRetry: PropTypes.bool,
  customStyles: PropTypes.object
};

export default ErrorBoundary;
